import { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Popover as MaterialPopover, Typography } from '@mui/material';

import { colors } from 'themes';

const PopoverText = styled(Typography)`
  padding: 8px 16px;
  font-size: 14px;
  white-space: pre-line;
`;

/**
 * Hover tooltip built on MUI `Popover`: wraps its children in a div and shows
 * a dark, non-interactive text bubble while the pointer is over them. Use for
 * short explanatory text on controls (e.g. why a button is disabled),
 * toggling `shouldDisplayPopover` to enable it.
 */
const Popover = ({
  children,
  horizontalPosition = 'left',
  shouldDisplayPopover,
  text,
  verticalPosition = 'top',
}) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const handlePopoverOpen = event => {
    setAnchorEl(event.currentTarget);
  };

  return (
    <>
      <div
        onMouseEnter={event => shouldDisplayPopover && handlePopoverOpen(event)}
        onMouseLeave={handlePopoverClose}
      >
        {children}
      </div>
      <MaterialPopover
        id="mouse-over-popover--send-button"
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        style={{ pointerEvents: 'none' }}
        anchorOrigin={{
          horizontal: horizontalPosition,
          vertical: verticalPosition,
        }}
        slotProps={{
          paper: {
            style: {
              backgroundColor: colors.gray70,
              color: colors.white,
            },
          },
        }}
        transformOrigin={{
          horizontal: 'right',
          vertical: 'bottom',
        }}
        onClose={handlePopoverClose}
        disableRestoreFocus
      >
        <PopoverText>{text}</PopoverText>
      </MaterialPopover>
    </>
  );
};

Popover.propTypes = {
  children: PropTypes.node.isRequired,
  /** MUI `anchorOrigin.horizontal` ('left', 'center', 'right'). */
  horizontalPosition: PropTypes.string,
  /** When false, hovering does nothing and the popover never opens. */
  shouldDisplayPopover: PropTypes.bool.isRequired,
  text: PropTypes.string.isRequired,
  /** MUI `anchorOrigin.vertical` ('top', 'center', 'bottom'). */
  verticalPosition: PropTypes.string,
};

export default Popover;
